// popupManager.js
export default class PopupManager {
  constructor(popups, currentNodeId = null) {
    this.popups = popups;
    this.currentNodeId = currentNodeId;
    // track popups that are currently loading or already shown
    this._loading = new Set();
    this._shown = new Set();
  }

  /**
   * Decide si un popup debe mostrarse según condiciones.
   */
  shouldShowPopup(popup) {
    let popupSetting = JSON.parse(popup.popup_settings);
    const conditions = popupSetting.conditions || [];
    let show = false;

    for (const condition of Object.values(conditions)) {
      const { action, type } = condition;

      if (type === 'entire_site') show = action === 'include';

      if (type === 'single_page' && Array.isArray(condition.pages)) {
        const match = condition.pages.includes(this.currentNodeId);
        if (match && action === 'include') show = true;
        if (match && action === 'exclude') show = false;
      }

      if (type === 'content_type' && Array.isArray(condition.types)) {
        const contentType = drupalSettings?.node?.type;
        const match = contentType && condition.types.includes(contentType);
        if (match && action === 'include') show = true;
        if (match && action === 'exclude') show = false;
      }
    }

    if (!this.canShowPopup(popup)) show = false;

    return show;
  }

  /**
   * Calcula tiempo de expiración en milisegundos.
   */
  calculateCloseDelay(close_action) {
    if (!close_action) return 7 * 24 * 60 * 60 * 1000;

    const { delay_value, delay_unit } = close_action;
    const value = parseInt(delay_value, 10) || 7;

    switch (delay_unit) {
      case 'seconds': return value * 1000;
      case 'minutes': return value * 60 * 1000;
      case 'hours':   return value * 60 * 60 * 1000;
      case 'days':    return value * 24 * 60 * 60 * 1000;
      default:        return 7 * 24 * 60 * 60 * 1000;
    }
  }

  /**
   * Comprueba si el popup puede mostrarse según localStorage.
   */
  canShowPopup(popup) {
    const closedUntil = localStorage.getItem(`popupClosedUntil_${popup.noahs_id}`);
    if (closedUntil && Date.now() < parseInt(closedUntil, 10)) return false;
    return true;
  }

  /**
   * Marca popup como cerrado en localStorage.
   */
  registerPopupClosed(popup) {
    const popupSetting = JSON.parse(popup.popup_settings);
    const delay = this.calculateCloseDelay(popupSetting.close_action);
    const expireAt = Date.now() + delay;
    localStorage.setItem(`popupClosedUntil_${popup.noahs_id}`, expireAt);
  }

  /**
   * Carga el HTML del popup y lo agrega al DOM.
   */
  async loadPopup(popup) {
    // Prevent duplicate loads: if already present in DOM or loading, skip
    const existing = document.querySelector(`#noahs-popup-${popup.noahs_id}`);
    if (existing) return;
    if (this._loading.has(popup.noahs_id) || this._shown.has(popup.noahs_id)) return;

    this._loading.add(popup.noahs_id);

    try {
      const loader = document.createElement('div');
      loader.classList.add('noahs-popup-loader');
      loader.innerHTML = `<div class="spinner-border" role="status">
                            <span class="visually-hidden">Loading...</span>
                          </div>`;
      document.body.appendChild(loader);

      const response = await fetch(`/noahs-popup/render/${popup.noahs_id}`);
      if (!response.ok) return;

      const html = await response.text();
      const wrapper = document.createElement('div');
      wrapper.innerHTML = html;

      const popupElement = wrapper.firstElementChild;
      if (!popupElement) {
        this._loading.delete(popup.noahs_id);
        return;
      }

  // Id consistente para poder cerrar desde botones
  popupElement.id = `noahs-popup-${popup.noahs_id}`;

      // Evento de cerrar
      popupElement.querySelector('.noahs_close_popup')?.addEventListener('click', () => {
        popupElement.remove();
        this.registerPopupClosed(popup);
        this._shown.delete(popup.noahs_id);
      });

      loader.remove();
      document.body.appendChild(popupElement);
      // mark as shown and clear loading
      this._shown.add(popup.noahs_id);
      this._loading.delete(popup.noahs_id);
    } catch (err) {
      this._loading.delete(popup.noahs_id);
      console.error(`Error al cargar popup ${popup.noahs_id}:`, err);
    }
  }

  /**
   * Ejecuta triggers automáticos de los popups.
   */
  runTriggers() {
    for (const popup of Object.values(this.popups)) {
      if (!this.shouldShowPopup(popup)) continue;

      const popupSetting = JSON.parse(popup.popup_settings);
      const triggers = popupSetting.triggers || {};

      // On Load
      if (triggers.on_load?.enabled === "true") {
        const delay = parseInt(triggers.on_load.delay || 0, 10) * 1000;
        setTimeout(() => this.loadPopup(popup), delay);
      }

      // On Scroll
      if (triggers.on_scroll?.enabled === "true") {
        const offset = parseInt(triggers.on_scroll.offset || 0, 10);
        const handler = () => {
          if (window.scrollY >= offset) {
            // prevent duplicate scroll-triggered loads
            if (!this._loading.has(popup.noahs_id) && !this._shown.has(popup.noahs_id)) {
              this.loadPopup(popup);
            }
            window.removeEventListener('scroll', handler);
          }
        };
        window.addEventListener('scroll', handler);
      }
    }
  }

  /**
   * Maneja los botones que abren o cierran popups manualmente.
   */
  runButtons() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-popup-action]');

      if (!btn) return;

      e.preventDefault();

      const action = btn.dataset.popupAction; // "open_popup" | "close_popup"
      const popupId = btn.dataset.popupSettings;
      console.log(`Button clicked: ${action}, Popup ID: ${popupId}`);
      if (!popupId) return;

      const popup = this.popups[popupId];
      if (!popup) return;

      if (action === 'open_popup') {
        this.loadPopup(popup); // ignorando triggers y condiciones
      }

      if (action === 'close_popup') {
        const el = document.querySelector(`#noahs-popup-${popupId}`);
        if (el) el.remove();
        this.registerPopupClosed(popup);
      }
    });
  }

  /**
   * Inicializa todo: triggers y botones manuales.
   */
  init() {
    this.runTriggers();
    this.runButtons();
  }
}
